import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Download,
  Eye,
  Send,
  Mail,
  FileText,
  Users,
  CheckCircle,
  AlertCircle,
  Plus,
  X,
  RefreshCw,
  ArrowLeft,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Trash2,
  RotateCcw,
  ChevronDown,
  Loader2
} from 'lucide-react';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import { payslipsApi } from '../../../api/payslipsApi';
import { payrollRunApi } from '../../../api/payrollRunApi';
import { employeeApi } from '../../../api/employeeApi';
import { toast } from 'react-hot-toast';
import { formatDate } from '../../../utils/stringUtils';
import { exportToCSV, generateExportFilename, downloadFromBackend } from '../../../utils/exportUtils';
import ExportModal from '../../../components/common/ExportModal';
import WarningPopup from '../../../components/WarningPopup';

const formatDateDisplay = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};


const formatDateRange = (start, end) => {
  const startFormatted = formatDateDisplay(start);
  const endFormatted = formatDateDisplay(end);
  if (!startFormatted && !endFormatted) return '';
  return `${startFormatted || 'N/A'} - ${endFormatted || 'N/A'}`;
};

const formatPeriodString = (periodString) => {
  if (!periodString) return 'N/A';

  // Parse period string like "01/11/2025 - 30/11/2025"
  const parts = periodString.split(' - ');
  if (parts.length !== 2) return periodString; // Return original if format is unexpected

  try {
    // Parse DD/MM/YYYY format
    const parseDate = (dateStr) => {
      const [day, month, year] = dateStr.trim().split('/');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    };

    const startDate = parseDate(parts[0]);
    const endDate = parseDate(parts[1]);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return periodString; // Return original if parsing fails
    }

    const startFormatted = formatDate(startDate, { includeTime: false });
    const endFormatted = formatDate(endDate, { includeTime: false });

    return `${startFormatted} - ${endFormatted}`;
  } catch (error) {
    return periodString; // Return original if any error occurs
  }
};

const formatMonthValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  // Extract year and month from the date
  // Backend stores dates in UTC, so we use UTC methods
  // For period end: Date.UTC(2025, 11, 0) = Nov 30, 2025 (day 0 of month 11 = last day of month 10 in 0-indexed)
  // But JavaScript months are 0-indexed, so getUTCMonth() returns 10 for November
  // We add 1 to get the 1-indexed month (11 for November)
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + 1; // UTC month is 0-indexed, add 1 for 1-12
  return `${year}-${String(month).padStart(2, '0')}`;
};

const EMPLOYEE_PAGE_SIZE = 20;

const PayslipManagement = (props) => {
  const { onViewPayslip } = props || {};
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // ... rest of state ...
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchParams.get('search') || '');
  const [filterPeriod, setFilterPeriod] = useState('current');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'all');
  const [filterStartDate, setFilterStartDate] = useState(searchParams.get('startDate') || null);
  const [filterEndDate, setFilterEndDate] = useState(searchParams.get('endDate') || null);
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [activeTab, setActiveTab] = useState('payslips');
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const initialGenerateFormData = {
    payGroupId: '',
    payrollMonth: '',
    type: 'all',
    selectedEmployeeIds: [],
    sendEmails: false
  };
  const [generateFormData, setGenerateFormData] = useState(initialGenerateFormData);
  const [sortField, setSortField] = useState(searchParams.get('sortBy') || null);
  const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || 'asc');
  const [generateErrors, setGenerateErrors] = useState({ payGroup: '' });
  const [payslips] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState('');
  const employeeListRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);

  // Sync filter state to URL search params
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      // Managed keys for this component
      const managedKeys = ['search', 'status', 'startDate', 'endDate', 'sortBy', 'sortOrder'];
      managedKeys.forEach(key => params.delete(key));

      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      if (filterStatus && filterStatus !== 'all') params.set('status', filterStatus);
      if (filterStartDate) params.set('startDate', filterStartDate);
      if (filterEndDate) params.set('endDate', filterEndDate);
      if (sortField) params.set('sortBy', sortField);
      if (sortOrder && sortOrder !== 'asc') params.set('sortOrder', sortOrder);

      return params;
    }, { replace: true });
  }, [debouncedSearchTerm, filterStatus, filterStartDate, filterEndDate, sortField, sortOrder, setSearchParams]);

  const baseExportFilters = useMemo(() => ({
    search: searchTerm,
    status: filterStatus,
    startDate: filterStartDate,
    endDate: filterEndDate,
  }), [searchTerm, filterStatus, filterStartDate, filterEndDate]);

  const requestBackendExport = React.useCallback(
    async ({
      scope,
      format,
      selectedIds,
      overrideFilters,
      overridePage,
      overrideLimit,
    }) => {
      const filters = overrideFilters ?? baseExportFilters;
      const payload = {
        module: 'payslip-batches',
        scope,
        format,
        filters,
        page: overridePage ?? 1,
        limit: overrideLimit ?? 100,
        sortBy: sortField || 'createdAt',
        sortOrder: sortOrder || 'desc',
        selectedIds: selectedIds ?? [],
      };

      const response = await authFetch(`${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Export failed');
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="?(.+?)"?$/);
      const fallbackName = `payslip-batches-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseExportFilters, sortField, sortOrder]
  );
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [batchToDelete, setBatchToDelete] = useState(null);

  // Bulk email job progress tracking
  const [emailJobState, setEmailJobState] = useState(null);
  // emailJobState shape: { batchId, jobId, status, total, sent, failed, errors }



  // Debounce main search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Debounce employee search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedEmployeeSearch(employeeSearch.trim());
    }, 400);
    return () => clearTimeout(handler);
  }, [employeeSearch]);

  // State for bulk email send confirmation
  const [confirmSendBatch, setConfirmSendBatch] = useState(null);
  // confirmSendBatch shape: { batchId, batchName, totalEmployees }

  const handleSendBatchClick = (batch) => {
    setConfirmSendBatch({
      batchId: batch.id,
      batchName: batch.name || batch.payName || 'this batch',
      totalEmployees: batch.totalEmployees || batch.generatedPayslips || 0
    });
  };

  const handleConfirmSendBatch = () => {
    if (!confirmSendBatch?.batchId) return;
    sendBatchEmailsMutation.mutate(confirmSendBatch.batchId);
    setConfirmSendBatch(null);
  };



  useEffect(() => {
    if (generateFormData.type === 'all' && generateFormData.selectedEmployeeIds.length > 0) {
      setGenerateFormData(prev => ({
        ...prev,
        selectedEmployeeIds: []
      }));
    }
  }, [generateFormData.type]);

  // Reset selection when filters change
  useEffect(() => {
    setSelectedRows(new Set());
  }, [debouncedSearchTerm, filterStatus, filterStartDate, filterEndDate]);

  // Fetch payslip batches from PayslipBatch (not PayrollRun)
  const {
    data: payslipBatchesData,
    isLoading: isBatchesLoading,
    refetch: refetchBatches
  } = useQuery({
    queryKey: ['payslipBatches', debouncedSearchTerm, filterStatus, filterStartDate, filterEndDate],
    queryFn: async () => {
      // Use PayslipBatch endpoint - payslip generation is separate from payroll run
      const response = await payslipsApi.getPayslipBatchList({
        search: debouncedSearchTerm || undefined,
        status: filterStatus !== 'all' ? filterStatus : undefined,
        startDate: filterStartDate || undefined,
        endDate: filterEndDate || undefined
      });

      if (response?.responseCode === 200 && Array.isArray(response.data)) {
        // Map PayslipBatch data to batch format
        const mappedBatches = response.data.map(batch => ({
          id: batch.id || batch._id,
          batchId: batch.batchId,
          payGroupId: batch.payGroupId,
          payGroupName: batch.payGroupName || 'N/A',
          payrollMonth: batch.payrollMonth,
          period: batch.period || 'N/A',
          status: batch.status || 'generated',
          totalEmployees: batch.totalEmployees || 0,
          totalNetPay: batch.totalNetPay || 0,
          totalGrossPay: batch.totalGrossPay || 0,
          totalDeductions: batch.totalDeductions || 0,
          generatedPayslips: batch.generatedPayslips || 0,
          failedPayslips: batch.failedPayslips || 0,
          generatedDate: batch.generatedDate || batch.createdAt,
          createdAt: batch.createdAt,
          updatedAt: batch.updatedAt
        }));

        return mappedBatches;
      }

      return [];
    },
    staleTime: 0,
    onError: (error) => {
      toast.error('Error loading payslip batches');
    }
  });


  const {
    data: payGroupOptions = [],
    isLoading: isPayGroupsLoading
  } = useQuery({
    queryKey: ['payrollRuns', 'recent', 10],
    queryFn: async () => {
      const response = await payrollRunApi.getPayrollRuns({ limit: 10 });

      if (response?.responseCode === 200 && Array.isArray(response.data)) {
        const uniqueGroups = [];
        const seen = new Set();

        for (const run of response.data) {
          const rawLabel = run?.payGroup?.groupName || run?.payGroup || run?.groupName || run?.runId;
          if (!rawLabel) continue;

          const range = formatDateRange(
            run?.payPeriodStart || run?.payPeriod?.startDate,
            run?.payPeriodEnd || run?.payPeriod?.endDate
          );

          const normalizedRange = range || '';
          const payGroupValue = run?.payGroupId || run?.payGroup || run?.groupName || run?.runId;
          // Use period end date to determine the payroll month, as it represents the actual month
          // Period start might be the last day of previous month in UTC, causing incorrect month extraction
          const payrollMonthValue = formatMonthValue(run?.payPeriodEnd || run?.payPeriod?.endDate || run?.payPeriodStart || run?.payPeriod?.startDate);
          const finalLabel = normalizedRange ? `${rawLabel} (${normalizedRange})` : rawLabel;

          const dedupeKey = `${payGroupValue}__${normalizedRange}`;
          if (seen.has(dedupeKey)) continue;
          seen.add(dedupeKey);

          uniqueGroups.push({
            id: payGroupValue,
            label: finalLabel,
            value: payGroupValue,
            period: normalizedRange,
            payrollMonth: payrollMonthValue
          });

          if (uniqueGroups.length === 10) break;
        }

        return uniqueGroups;
      }

      throw new Error(response?.responseMessage || 'Failed to load pay groups');
    },
    staleTime: 0,
    onError: (error) => {
      toast.error('Error loading pay groups');
    }
  });

  const {
    data: employeePages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: employeesLoading,
    refetch: refetchEmployees
  } = useInfiniteQuery({
    queryKey: ['payrollEmployees', debouncedEmployeeSearch],
    enabled: showGenerateModal && generateFormData.type === 'individual',
    queryFn: async ({ pageParam = 1 }) => {
      const response = await employeeApi.getEmployeePicker({
        page: pageParam,
        limit: EMPLOYEE_PAGE_SIZE,
        search: debouncedEmployeeSearch
      });
      const payload = response?.data || response || {};
      const employees = payload?.employees || payload?.data?.employees || [];
      const pagination = payload?.pagination || payload?.data?.pagination || {
        page: pageParam,
        hasNextPage: false
      };
      return { employees, pagination };
    },
    getNextPageParam: (lastPage) => {
      if (lastPage?.pagination?.hasNextPage) {
        return (lastPage.pagination.page || 1) + 1;
      }
      return undefined;
    },
    staleTime: 0
  });

  const employeeList = useMemo(() => {
    if (!employeePages?.pages?.length) return [];
    return employeePages.pages.flatMap(page => page.employees || []);
  }, [employeePages]);

  const handlePayGroupChange = (value) => {
    const selected = payGroupOptions.find(group => group.value === value);
    // Ensure payrollMonth is set from the selected pay group
    const payrollMonth = selected?.payrollMonth || '';
    setGenerateFormData(prev => ({
      ...prev,
      payGroupId: value,
      payrollMonth: payrollMonth,
      selectedEmployeeIds: prev.selectedEmployeeIds
    }));

    setGenerateErrors(prev => ({
      ...prev,
      payGroup: value ? '' : 'Please select a pay group.'
    }));
  };

  const handleGenerateFormChange = (field, value) => {
    setGenerateFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleEmployeeSelection = (employeeId) => {
    setGenerateFormData(prev => {
      const exists = prev.selectedEmployeeIds.includes(employeeId);
      return {
        ...prev,
        selectedEmployeeIds: exists
          ? prev.selectedEmployeeIds.filter(id => id !== employeeId)
          : [...prev.selectedEmployeeIds, employeeId]
      };
    });
  };

  const handleEmployeeScroll = (event) => {
    if (!hasNextPage || isFetchingNextPage) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 48) {
      fetchNextPage();
    }
  };

  const downloadPayslipMutation = useMutation({
    mutationFn: async (payslipId) => {
      const response = await payslipsApi.downloadPayslip(payslipId);
      if (response.responseCode !== 200) {
        throw new Error(response.responseMessage || 'Failed to download payslip');
      }
      return response;
    },
    onSuccess: () => {
      toast.success('Payslip downloaded successfully');
    },
    onError: (error) => {
      toast.error('Error downloading payslip');
    }
  });

  const sendPayslipEmailMutation = useMutation({
    mutationFn: async (payslipId) => {
      const response = await payslipsApi.sendPayslipEmail(payslipId);
      if (response.responseCode !== 200) {
        throw new Error(response.responseMessage || 'Failed to send payslip email');
      }
      return response;
    },
    onSuccess: () => {
      toast.success('Payslip email sent successfully');
    },
    onError: (error) => {
      toast.error('Error sending payslip email');
    }
  });

  // Track which batch is currently sending emails
  const [sendingBatchId, setSendingBatchId] = useState(null);

  const sendBatchEmailsMutation = useMutation({
    mutationFn: async (batchId) => {
      setSendingBatchId(batchId);
      const response = await payslipsApi.sendBatchEmails(batchId);
      if (response.responseCode !== 200) {
        throw new Error(response.responseMessage || 'Failed to start email job');
      }
      return { batchId, ...response };
    },
    onSuccess: (response) => {
      const data = response.data || {};
      const jobId = data.jobId;
      const batchId = response.batchId;
      // Start progress tracking
      setEmailJobState({
        batchId,
        jobId,
        status: 'processing',
        total: data.total || 0,
        sent: 0,
        failed: 0,
        errors: []
      });
      toast.success('Sending payslips in background. You can continue working.');
    },
    onError: (error) => {
      toast.error(error?.message || 'Error starting email job');
      setSendingBatchId(null);
    }
  });

  // Poll email job progress every 3 seconds while processing
  useEffect(() => {
    if (!emailJobState?.batchId || !emailJobState?.jobId) return;
    if (emailJobState.status === 'completed' || emailJobState.status === 'failed') return;

    const interval = setInterval(async () => {
      try {
      const res = await payslipsApi.getEmailJobStatus(emailJobState.batchId);
      const data = res?.data || {};
      setEmailJobState(prev => ({ ...prev, ...data }));

      if (data.status === 'completed' || data.status === 'failed') {
        clearInterval(interval);
        setSendingBatchId(null);
        refetchBatches();
        if (data.status === 'completed') {
          if (data.failed === 0) {
            toast.success(`✅ Payslip emails sent to all ${data.sent} employees with PDF attachments!`);
          } else {
            toast.success(`Sent to ${data.sent} employees. ${data.failed} failed.`, { duration: 5000 });
          }
        } else {
          toast.error('Bulk email job encountered an error. Check failed list for details.');
        }
        }
      } catch (error) {
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [emailJobState, refetchBatches]);

  const deleteBatchMutation = useMutation({
    mutationFn: async (batchId) => {
      const response = await payslipsApi.deletePayslipBatch(batchId);
      if (response && response.responseCode !== 200 && response.responseCode !== undefined && response.responseCode !== null) {
        throw new Error(response.responseMessage || 'Failed to delete payslip batch');
      }
      return response;
    },
    onSuccess: () => {
      // toast.success('Payslip batch deleted successfully'); // Removed to avoid duplicate notifications
      refetchBatches();
    },
    onError: (error) => {
      toast.error(error?.message || 'Error deleting payslip batch');
    }
  });

  const handleDeleteBatch = (batchId) => {
    setBatchToDelete(batchId);
    setIsBulkDelete(false);
    setDeleteConfirmOpen(true);
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setIsBulkDelete(true);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteBatch = async () => {
    if (!batchToDelete && !isBulkDelete) return;

    try {
      if (isBulkDelete) {
        const ids = Array.from(selectedRows);
        let successCount = 0;
        let failCount = 0;

        for (const id of ids) {
          try {
            await deleteBatchMutation.mutateAsync(id);
            successCount++;
          } catch (error) {
            failCount++;
          }
        }

        if (successCount > 0) {
          toast.success(`${successCount} payslip batch(s) deleted successfully.`);
          setSelectedRows(new Set());
        }
        if (failCount > 0) {
          toast.error(`${failCount} payslip batch(s) could not be deleted.`);
        }
      } else {
        await deleteBatchMutation.mutateAsync(batchToDelete);
        toast.success('Payslip batch deleted successfully.');
      }
      setDeleteConfirmOpen(false);
      setBatchToDelete(null);
      setIsBulkDelete(false);
    } catch (error) {
      if (!isBulkDelete) {
        toast.error('Failed to delete payslip batch. Please try again.');
      }
    }
  };

  const cancelDeleteBatch = () => {
    setDeleteConfirmOpen(false);
    setBatchToDelete(null);
    setIsBulkDelete(false);
  };

  const generatePayslipsMutation = useMutation({
    retry: false,
    mutationFn: async (payload) => {
      // apiClient interceptor will throw Error for non-2xx responses
      // So we just call the API and let errors propagate naturally
      const response = await payslipsApi.generatePayslips(payload);

      // If we get here, it's a success (2xx response)
      // apiClient interceptor only returns response.data for 2xx, and throws Error for others
      if (response && response.responseCode >= 200 && response.responseCode < 300) {
        return response;
      }

      // This should rarely happen since apiClient throws for non-2xx
      const errorMessage = response?.responseMessage || response?.error || 'Failed to generate payslips';
      throw new Error(errorMessage);
    },
    onSuccess: (response) => {
      // Reset submission flag first
      isSubmittingRef.current = false;

      // Extract summary data
      const summary = response?.data || {};
      const generatedCount = summary.totalEmployees ?? 0;
      const monthLabel = summary.month ? ` for ${summary.month}` : '';

      // Close modal immediately - do this synchronously
      setShowGenerateModal(false);

      // Reset form data
      setGenerateFormData(initialGenerateFormData);

      // Refetch batches to show the new batch in table
      refetchBatches();
      // Also invalidate payroll runs query to refresh data
      queryClient.invalidateQueries(['payrollRuns']);

      // Show success toast
      toast.success(`Generated ${generatedCount} payslips${monthLabel}`);
    },
    onError: (error) => {
      // Extract error message from various possible locations
      // Priority: error.message (from apiClient interceptor) > response.data > fallback
      let errorMessage = 'An error occurred while generating payslips. Please try again.';

      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.response?.data?.responseMessage) {
        errorMessage = error.response.data.responseMessage;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      // Ensure toast is called
      try {
        toast.error(errorMessage, {
          duration: 5000, // Show for 5 seconds for important errors
        });
      } catch (toastError) {
        // Fallback: use alert if toast fails
        alert(errorMessage);
      }

      isSubmittingRef.current = false; // Reset flag on error
    }
  });
  const isGeneratingPayslips = generatePayslipsMutation.isPending;

  // Close modal when mutation succeeds (backup mechanism)
  useEffect(() => {
    if (generatePayslipsMutation.isSuccess && showGenerateModal) {
      // Use setTimeout to ensure state updates happen after render
      setTimeout(() => {
        setShowGenerateModal(false);
        setGenerateFormData(initialGenerateFormData);
        isSubmittingRef.current = false;
      }, 0);
    }
  }, [generatePayslipsMutation.isSuccess, showGenerateModal]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'withheld': return 'bg-red-100 text-red-800';
      case 'archived': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleViewPayslip = (payslip) => {
    if (onViewPayslip) {
      onViewPayslip(payslip?._id || payslip?.id);
    } else {
      setSelectedPayslip(payslip);
      setShowPayslipModal(true);
    }
  };

  const handleDownloadPayslip = (payslipId) => {
    if (!payslipId) return;
    downloadPayslipMutation.mutate(payslipId);
  };

  const handleSendEmail = (payslipId) => {
    if (!payslipId) return;
    sendPayslipEmailMutation.mutate(payslipId);
  };







  const handleGeneratePayslips = () => {
    setShowGenerateModal(true);
  };

  const handleSubmitGenerate = () => {
    // Prevent double submission
    if (isSubmittingRef.current || isGeneratingPayslips) {
      return;
    }

    const currentErrors = {
      payGroup: !generateFormData.payGroupId ? 'Please select a pay group.' : ''
    };
    setGenerateErrors(currentErrors);
    if (currentErrors.payGroup) {
      toast.error('Please fix the highlighted fields.');
      return;
    }

    if (!generateFormData.payrollMonth) {
      toast.error('Unable to determine payroll month for the selected pay group.');
      return;
    }

    if (generateFormData.type === 'individual' && generateFormData.selectedEmployeeIds.length === 0) {
      toast.error('Select at least one employee.');
      return;
    }

    const generateData = {
      payGroupId: generateFormData.payGroupId,
      payrollMonth: generateFormData.payrollMonth,
      type: generateFormData.type,
      employeeIds: generateFormData.type === 'individual' ? generateFormData.selectedEmployeeIds : [],
      sendEmails: generateFormData.sendEmails
    };

    // Set submitting flag
    isSubmittingRef.current = true;

    generatePayslipsMutation.mutate(generateData);
  };


  const filteredBatches = useMemo(() => {
    return payslipBatchesData || [];
  }, [payslipBatchesData]);

  // selection handlers
  const handleSelectAll = (e) => {
    const currentPageIds = sortedBatches.map(batch => batch.id);
    const selectedOnPage = currentPageIds.filter(id => selectedRows.has(id));

    if (selectedOnPage.length > 0) {
      // If any selected on current page, clear EVERYTHING
      setSelectedRows(new Set());
    } else {
      // Otherwise select all on current page
      const newSelected = new Set(selectedRows);
      currentPageIds.forEach(id => newSelected.add(id));
      setSelectedRows(newSelected);
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  // Helper to map batches for export - but the requirement says individual payslips.
  // We will fetch individual payslips for the selected batches if needed,
  // or use the backend export for all data.
  // However, the pattern from LocationManagement is to map the currently visible rows.
  // If we want to map individual payslips from batches, we'd need to fetch them.

  const sortedBatches = useMemo(() => {
    if (!filteredBatches) return [];
    if (!sortField) return filteredBatches;

    return [...filteredBatches].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'payGroupName':
          aValue = (a.payGroupName || '').toLowerCase();
          bValue = (b.payGroupName || '').toLowerCase();
          break;
        case 'period':
          // Prefer payrollMonth for sorting if available, else period string
          aValue = (a.payrollMonth || a.period || '').toLowerCase();
          bValue = (b.payrollMonth || b.period || '').toLowerCase();
          break;
        case 'totalNetPay':
          aValue = Number(a.totalNetPay || 0);
          bValue = Number(b.totalNetPay || 0);
          break;
        case 'totalEmployees':
          aValue = Number(a.totalEmployees || 0);
          bValue = Number(b.totalEmployees || 0);
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        case 'generatedDate':
          aValue = new Date(a.generatedDate || 0).getTime();
          bValue = new Date(b.generatedDate || 0).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredBatches, sortField, sortOrder]);

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats(!showInlineSelectedFormats);
    } else {
      setShowExportModal(true);
    }
  };

  const handleExportSelected = async (format) => {
    if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
    const ids = Array.from(selectedRows);
    if (!ids.length) {
      toast.error('No selected batches found to export.');
      return;
    }
    setExportLoading(true);
    const toastId = toast.loading(`Preparing ${format.toUpperCase()} export...`);
    try {
      await requestBackendExport({
        scope: 'selected',
        format,
        selectedIds: ids,
      });
      toast.success(`${ids.length} batch(es) exported!`, { id: toastId });
      setShowInlineSelectedFormats(false);
    } catch (error) {
      toast.error(error?.message || 'Export failed.', { id: toastId });
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportScoped = React.useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      setExportLoading(true);
      const toastId = toast.loading(`Preparing ${format.toUpperCase()} export...`);
      try {
        if (scope === 'currentPage' && !sortedBatches.length) {
          toast.error('No data on current page to export.', { id: toastId });
          return;
        }

        if (scope === 'dateRange') {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error('Please select both start and end dates.', { id: toastId });
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: { ...baseExportFilters, startDate, endDate },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
          });
        } else {
          toast.dismiss(toastId);
          return;
        }

        toast.success('Export completed successfully', { id: toastId });
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed.', { id: toastId });
      } finally {
        setExportLoading(false);
      }
    },
    [sortedBatches, baseExportFilters, requestBackendExport]
  );

  const handleViewBatch = (batchId) => {
    navigate(`/payslips/batch/${batchId}`);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };



  const PayslipModal = () => {
    // Filter out deductions that might be incorrectly categorized as earnings
    const deductionKeywords = ['tax', 'tds', 'income tax', 'deduction', 'pf', 'provident fund', 'esi', 'professional tax'];
    const isDeduction = (name) => {
      const lowerName = (name || '').toLowerCase();
      return deductionKeywords.some(keyword => lowerName.includes(keyword));
    };

    // Separate earnings and deductions correctly
    const actualEarnings = (selectedPayslip?.components?.earnings || []).filter(earning => !isDeduction(earning.name));
    const misclassifiedDeductions = (selectedPayslip?.components?.earnings || []).filter(earning => isDeduction(earning.name));
    const actualDeductions = [
      ...(selectedPayslip?.components?.deductions || []),
      ...misclassifiedDeductions
    ];

    // Calculate total deductions from all deduction components
    const calculatedTotalDeductions = actualDeductions.reduce((sum, deduction) => {
      return sum + (deduction.amount || 0);
    }, 0);

    // Use calculated deductions if backend totalDeductions is 0 but we have deduction components
    const totalDeductions = (selectedPayslip?.totalDeductions > 0 && selectedPayslip.totalDeductions >= calculatedTotalDeductions)
      ? selectedPayslip.totalDeductions
      : calculatedTotalDeductions;

    // Calculate total earnings from actual earnings only
    const calculatedTotalEarnings = actualEarnings.reduce((sum, earning) => {
      return sum + (earning.amount || 0);
    }, 0);

    // Components are monthly (SalaryStructure); yearly = monthly × 12 for reference
    const monthlyGrossPay = Math.round(calculatedTotalEarnings * 100) / 100;
    const monthlyTotalDeductions = Math.round(totalDeductions * 100) / 100;
    const structureMonthlyNet = Number(selectedPayslip?.salaryStructure?.netPay) || 0;
    const monthlyNetPay =
      structureMonthlyNet > 0
        ? structureMonthlyNet
        : Math.round(Math.max(0, monthlyGrossPay - calculatedTotalDeductions) * 100) / 100;
    const annualGrossPay = Math.round(monthlyGrossPay * 12 * 100) / 100;
    const annualTotalDeductions = Math.round(monthlyTotalDeductions * 12 * 100) / 100;
    const yearlyNetPay = Math.round(monthlyNetPay * 12 * 100) / 100;
    const actualPayThisMonth = selectedPayslip?.netPay || 0;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6 gap-4">
              <h2 className="text-2xl font-bold text-gray-900 break-words whitespace-normal word-break flex-1 min-w-0">Payslip - {selectedPayslip?.employeeName || 'N/A'}</h2>
              <button
                onClick={() => setShowPayslipModal(false)}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Payslip Header */}
            <div className="bg-gradient-to-r from-agrosaf-green to-agrosaf-blue rounded-lg p-6 text-white mb-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold break-words">{process.env.REACT_APP_COMPANY_NAME}</h3>
                  <p className="text-sm opacity-90 break-words">Payslip for {selectedPayslip?.period || 'N/A'}</p>
                </div>
                <div className="text-right flex-1 min-w-0">
                  <p className="text-sm opacity-90 break-words whitespace-normal">Employee ID: {selectedPayslip?.employeeId || 'N/A'}</p>
                  <p className="text-sm opacity-90 break-words whitespace-normal word-break break-all">Department: {selectedPayslip?.department || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Employee Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Employee Information</h4>
                <div className="space-y-2">
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-600 flex-shrink-0">Name:</span>
                    <span className="font-medium break-words whitespace-normal word-break break-all text-right flex-1 min-w-0">{selectedPayslip?.employeeName || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-600 flex-shrink-0">Employee ID:</span>
                    <span className="font-medium break-words text-right">{selectedPayslip?.employeeId || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-600 flex-shrink-0">Department:</span>
                    <span className="font-medium break-words whitespace-normal word-break break-all text-right flex-1 min-w-0">{selectedPayslip?.department || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-600 flex-shrink-0">Pay Period:</span>
                    <span className="font-medium break-words text-right">{selectedPayslip?.period || 'N/A'}</span>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Pay Summary</h4>
                <div className="space-y-4">
                  {/* Actual Pay for This Month */}
                  <div className="pb-3 border-b-2 border-blue-300 bg-blue-50 rounded p-3">
                    <p className="text-xs font-semibold text-blue-800 mb-2 uppercase">Actual Pay for This Month</p>
                    <div className="space-y-2">
                      <div className="flex justify-between gap-2">
                        <span className="text-gray-700 flex-shrink-0 text-sm font-medium">Amount Payable:</span>
                        <span className="font-bold text-blue-700 break-words text-right text-base">₹{actualPayThisMonth.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      {selectedPayslip?.incentive > 0 && (
                        <div className="flex justify-between gap-2">
                          <span className="text-gray-700 flex-shrink-0 text-sm font-medium">Incentive:</span>
                          <span className="font-bold text-green-700 break-words text-right text-sm">₹{selectedPayslip.incentive.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      )}
                      {selectedPayslip?.presentDays !== undefined && selectedPayslip?.workingDays !== undefined && (
                        <div className="text-xs text-gray-600 mt-1">
                          Based on {selectedPayslip.presentDays} working days out of {selectedPayslip.workingDays} days
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Monthly Salary Structure */}
                  <div className="pb-2 border-b border-gray-300">
                    <p className="text-xs font-semibold text-gray-700 mb-2 uppercase">Salary Structure (Monthly)</p>
                    <div className="space-y-2">
                      <div className="flex justify-between gap-2">
                        <span className="text-gray-600 flex-shrink-0 text-sm">Gross Pay:</span>
                        <span className="font-medium text-green-600 break-words text-right text-sm">₹{monthlyGrossPay.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-gray-600 flex-shrink-0 text-sm">Total Deductions:</span>
                        <span className="font-medium text-red-600 break-words text-right text-sm">₹{monthlyTotalDeductions.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between border-t pt-2 gap-2">
                        <span className="text-gray-900 font-semibold flex-shrink-0 text-sm">Net Pay:</span>
                        <span className="font-bold text-blue-600 break-words text-right text-sm">₹{monthlyNetPay.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  </div>

                  {/* Yearly Salary Structure */}
                  <div className="pt-2">
                    <p className="text-xs font-semibold text-gray-700 mb-2 uppercase">Salary Structure (Yearly)</p>
                    <div className="space-y-2">
                      <div className="flex justify-between gap-2">
                        <span className="text-gray-600 flex-shrink-0 text-sm">Gross Pay:</span>
                        <span className="font-medium text-green-600 break-words text-right text-sm">₹{annualGrossPay.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between gap-2">
                        <span className="text-gray-600 flex-shrink-0 text-sm">Total Deductions:</span>
                        <span className="font-medium text-red-600 break-words text-right text-sm">₹{annualTotalDeductions.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      <div className="flex justify-between border-t pt-2 gap-2">
                        <span className="text-gray-900 font-semibold flex-shrink-0 text-sm">Net Pay:</span>
                        <span className="font-bold text-blue-600 break-words text-right text-sm">₹{yearlyNetPay.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Earnings and Deductions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Earnings */}
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="p-4 border-b border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-900">Earnings</h4>
                </div>
                <div className="p-4">
                  <div className="space-y-3">
                    {actualEarnings.length > 0 ? (
                      actualEarnings.map((earning, index) => {
                        const monthlyAmount = earning.amount || 0;
                        return (
                          <div key={index} className="flex justify-between items-center gap-2">
                            <span className="text-gray-700 break-words flex-1">{earning.name || 'N/A'}</span>
                            <span className="font-medium text-green-600 flex-shrink-0">₹{monthlyAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-gray-500 text-sm">No earnings listed</div>
                    )}
                  </div>
                  <div className="border-t pt-3 mt-3">
                    <div className="flex justify-between items-center gap-2">
                      <span className="font-semibold text-gray-900 break-words flex-1">Total Earnings</span>
                      <span className="font-bold text-green-600 flex-shrink-0">₹{monthlyGrossPay.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Deductions */}
              <div className="bg-white border border-gray-200 rounded-lg">
                <div className="p-4 border-b border-gray-200">
                  <h4 className="text-lg font-semibold text-gray-900">Deductions</h4>
                </div>
                <div className="p-4">
                  <div className="space-y-3">
                    {actualDeductions.length > 0 ? (
                      actualDeductions.map((deduction, index) => {
                        const monthlyAmount = deduction.amount || 0;
                        return (
                          <div key={index} className="flex justify-between items-center gap-2">
                            <span className="text-gray-700 break-words flex-1">{deduction.name || 'N/A'}</span>
                            <span className="font-medium text-red-600 flex-shrink-0">₹{monthlyAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-gray-500 text-sm">No deductions listed</div>
                    )}
                  </div>
                  <div className="border-t pt-3 mt-3">
                    <div className="flex justify-between items-center gap-2">
                      <span className="font-semibold text-gray-900 break-words flex-1">Total Deductions</span>
                      <span className="font-bold text-red-600 flex-shrink-0">₹{monthlyTotalDeductions.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-6 text-center text-sm text-gray-500">
              <p>This is a computer-generated payslip and does not require a signature.</p>
              <p>Generated on: {(() => {
                const dateValue = selectedPayslip?.generatedDate || selectedPayslip?.createdAt;
                if (!dateValue) return 'N/A';
                try {
                  const date = new Date(dateValue);
                  if (Number.isNaN(date.getTime())) return 'N/A';
                  return date.toLocaleString('en-IN', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                } catch (error) {
                  return 'N/A';
                }
              })()}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const isIndividualMode = generateFormData.type === 'individual';
  const isGenerateDisabled =
    !!generateErrors.payGroup ||
    !generateFormData.payGroupId ||
    !generateFormData.payrollMonth ||
    isGeneratingPayslips ||
    isPayGroupsLoading ||
    (isIndividualMode && generateFormData.selectedEmployeeIds.length === 0);


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payslip Management</h1>
          <p className="text-gray-600 mt-1">Generate, manage and distribute employee payslips</p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-4">
          {selectedRows.size > 0 && (
            <PermissionGate permission="payroll" action="delete">
              <Button
                variant="primary"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={handleBulkDelete}
                className="bg-red-600 hover:bg-red-700 border-red-600"
              >
                Delete ({selectedRows.size})
              </Button>
            </PermissionGate>
          )}
          <PermissionGate permission="payroll" action="read">
            <div className="relative">
              <Button
                variant="outline"
                icon={exportLoading ? <Loader2 className="h-4 w-4 animate-spin text-blue-600" /> : <Download className="h-4 w-4" />}
                onClick={handleExportClick}
                disabled={exportLoading}
                className="border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                Export {selectedRows.size > 0 ? `(${selectedRows.size})` : ''}
                <ChevronDown className={`ml-1 h-4 w-4 transition-transform ${showInlineSelectedFormats ? 'rotate-180' : ''}`} />
              </Button>

              <SelectedExportFormatBar
                isOpen={showInlineSelectedFormats}
                onClose={() => setShowInlineSelectedFormats(false)}
                onExport={handleExportSelected}
                loading={exportLoading}
                className="absolute right-0 mt-2 z-50 min-w-[200px]"
              />
            </div>
          </PermissionGate>
          <PermissionGate permission="payroll" action="create">
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
              onClick={() => setShowGenerateModal(true)}
            >
              Generate Payslips
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-grow min-w-[300px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by Pay Group..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Status:</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
            >
              <option value="all">All Status</option>
              <option value="generated">Generated</option>
              <option value="sent">Sent</option>
              <option value="completed">Completed</option>
              <option value="processing">Processing</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <div className="flex-shrink-0">
            <DateRangeFilter
              startDate={filterStartDate}
              endDate={filterEndDate}
              allowFutureDates={false}
              onChange={(range) => {
                setFilterStartDate(range.startDate);
                setFilterEndDate(range.endDate);
              }}
            />
          </div>

          {(searchTerm || filterStatus !== 'all' || filterStartDate || filterEndDate) && (
            <div className="flex-shrink-0">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterStatus('all');
                  setFilterStartDate(null);
                  setFilterEndDate(null);
                }}
                className="flex items-center space-x-2 px-4 py-[9px] border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                title="Clear all filters"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Clear</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Payslip Batches List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green"
                    checked={sortedBatches?.length > 0 && selectedRows.size === sortedBatches.length}
                    onChange={handleSelectAll}
                  />
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('payGroupName')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Pay Group Name</span>
                    {sortField === 'payGroupName' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-blue-600" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('period')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Period</span>
                    {sortField === 'period' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-blue-600" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('totalNetPay')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Net Pay</span>
                    {sortField === 'totalNetPay' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-blue-600" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('totalEmployees')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Total Staff</span>
                    {sortField === 'totalEmployees' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-blue-600" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    {sortField === 'status' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-blue-600" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('generatedDate')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Generated At</span>
                    {sortField === 'generatedDate' ? (
                      sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-blue-600" />
                    ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isBatchesLoading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-agrosaf-green"></div>
                      <span className="ml-2 text-gray-500">Loading payslip batches...</span>
                    </div>
                  </td>
                </tr>
              ) : payslipBatchesData && payslipBatchesData.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    No payslip batches found
                  </td>
                </tr>
              ) : (
                sortedBatches?.map((batch) => (
                  <tr key={batch.id} className={`hover:bg-gray-50 ${selectedRows.has(batch.id) ? 'bg-blue-50/50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green"
                        checked={selectedRows.has(batch.id)}
                        onChange={() => handleSelectRow(batch.id)}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{batch.payGroupName || 'N/A'}</div>
                      {batch.payrollMonth && (
                        <div className="text-xs text-gray-500">{batch.payrollMonth}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatPeriodString(batch.period)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ₹{(batch.totalNetPay || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {batch.totalEmployees || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${batch.status === 'completed' ? 'bg-green-100 text-green-800' :
                        batch.status === 'sent' ? 'bg-purple-100 text-purple-800' :
                          batch.status === 'generated' ? 'bg-blue-100 text-blue-800' :
                            batch.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                              batch.status === 'failed' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                        }`}>
                        {batch.status || 'generated'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {batch.generatedDate ? formatDate(batch.generatedDate, { includeTime: true }) : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <PermissionGate permission="payroll" action="read">
                          <button
                            onClick={() => handleViewBatch(batch.id)}
                            className="text-blue-600 hover:text-blue-900 flex items-center space-x-1"
                            title="View Payslips"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </PermissionGate>
                        <PermissionGate permission="payroll" action="create">
                          <button
                            onClick={() => handleSendBatchClick(batch)}
                            disabled={sendingBatchId !== null}
                            className="text-green-600 hover:text-green-900 flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Send Payslip Emails to All Employees"
                          >
                            {sendingBatchId === batch.id ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <Mail className="h-4 w-4" />
                            )}
                          </button>
                        </PermissionGate>
                        <PermissionGate permission="payroll" action="delete">
                          <button
                            onClick={() => handleDeleteBatch(batch.id)}
                            disabled={deleteBatchMutation.isPending && deleteBatchMutation.variables === batch.id}
                            className="text-red-500 hover:text-red-800 flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed ml-2"
                            title="Delete Batch"
                          >
                            {deleteBatchMutation.isPending && deleteBatchMutation.variables === batch.id ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        </PermissionGate>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Empty State */}
      {!isBatchesLoading && payslipBatchesData && payslipBatchesData.length === 0 && (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No payslip batches found</h3>
          <p className="text-gray-500 mb-4">
            Generate payslips for the current period to get started.
          </p>
          <PermissionGate permission="payroll" action="create">
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
              onClick={handleGeneratePayslips}
            >
              Generate Payslips
            </Button>
          </PermissionGate>
        </div>
      )}

      {/* Modals */}
      {showPayslipModal && <PayslipModal />}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Payslip Batches"
        scopedFormatExport={{
          onExport: handleExportScoped,
          showDateRange: true,
        }}
      />

      {/* Delete Confirmation Modal */}
      <WarningPopup
        isOpen={deleteConfirmOpen}
        onClose={cancelDeleteBatch}
        onConfirm={confirmDeleteBatch}
        title={isBulkDelete ? "Delete Payslip Batches" : "Delete Payslip Batch"}
        message={
          isBulkDelete
            ? `Are you sure you want to delete ${selectedRows.size} selected payslip batch(es)? All associated payslips will also be deleted.`
            : "Are you sure you want to delete this payslip batch? All associated payslips will also be deleted."
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      {/* Generate Payslips Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Generate Payslips</h2>
                <button
                  onClick={() => setShowGenerateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Pay Group Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pay Group <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={generateFormData.payGroupId}
                    onChange={(e) => handlePayGroupChange(e.target.value)}
                    disabled={isPayGroupsLoading || payGroupOptions.length === 0}
                    className={`w-full px-3 py-2 border ${generateErrors.payGroup ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent disabled:bg-gray-100`}
                  >
                    <option value="">Select Pay Group</option>
                    {payGroupOptions.map((group) => (
                      <option key={`${group.value}-${group.period || 'na'}`} value={group.value}>
                        {group.label}
                      </option>
                    ))}
                  </select>
                  <div className={`mt-1 text-xs ${generateErrors.payGroup ? 'text-red-600' : 'text-gray-500'}`}>
                    {generateErrors.payGroup || 'Showing the most recent 10 payroll runs.'}
                  </div>
                  {isPayGroupsLoading && (
                    <p className="text-xs text-gray-500 mt-1">Loading pay groups...</p>
                  )}
                  {!isPayGroupsLoading && payGroupOptions.length === 0 && (
                    <p className="text-xs text-red-500 mt-1">No payroll runs available. Create one to enable payslip generation.</p>
                  )}
                </div>

                {/* Employee Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employee Selection
                  </label>
                  <div className="flex flex-wrap gap-6">
                    <label className="flex items-center text-sm text-gray-700">
                      <input
                        type="radio"
                        name="employeeSelection"
                        value="all"
                        checked={!isIndividualMode}
                        onChange={() => handleGenerateFormChange('type', 'all')}
                        className="mr-2 text-agrosaf-green focus:ring-agrosaf-green"
                      />
                      <span>All Employees</span>
                    </label>
                    <label className="flex items-center text-sm text-gray-700">
                      <input
                        type="radio"
                        name="employeeSelection"
                        value="individual"
                        checked={isIndividualMode}
                        onChange={() => handleGenerateFormChange('type', 'individual')}
                        className="mr-2 text-agrosaf-green focus:ring-agrosaf-green"
                      />
                      <span>Individual Employee</span>
                    </label>
                  </div>
                </div>

                {isIndividualMode ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          value={employeeSearch}
                          onChange={(e) => setEmployeeSearch(e.target.value)}
                          placeholder="Search by name, email, or employee ID"
                          className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
                        />
                      </div>
                      <Button
                        variant="secondary"
                        onClick={() => refetchEmployees()}
                        disabled={employeesLoading}
                        className="flex items-center gap-2"
                      >
                        <RefreshCw className={`h-4 w-4 ${employeesLoading ? 'animate-spin' : ''}`} />
                        Refresh
                      </Button>
                    </div>

                    <div
                      ref={employeeListRef}
                      onScroll={handleEmployeeScroll}
                      className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100"
                    >
                      {employeesLoading && employeeList.length === 0 ? (
                        <div className="flex items-center justify-center py-6">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-agrosaf-green"></div>
                        </div>
                      ) : (
                        <>
                          {employeeList.map((employee) => {
                            const isChecked = generateFormData.selectedEmployeeIds.includes(employee.id);
                            return (
                              <label
                                key={employee.id}
                                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50"
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => toggleEmployeeSelection(employee.id)}
                                  className="text-agrosaf-green focus:ring-agrosaf-green"
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900">
                                    {employee.name || 'Unnamed Employee'}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {employee.employeeId || 'N/A'} • {employee.email || 'No email'}
                                  </p>
                                  <p className="text-xs text-gray-400">{employee.department || 'N/A'}</p>
                                </div>
                              </label>
                            );
                          })}
                          {employeeList.length === 0 && !employeesLoading && (
                            <div className="py-6 text-center text-sm text-gray-500">
                              No employees found for the current search.
                            </div>
                          )}
                          {hasNextPage && (
                            <div className="py-3 text-center text-xs text-gray-500">
                              {isFetchingNextPage ? 'Loading more employees...' : 'Scroll to load more'}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {generateFormData.selectedEmployeeIds.length} employee(s) selected
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-dashed border-gray-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-1">Automatic Selection</h4>
                    <p className="text-sm text-gray-600">
                      All active employees will be considered for payslip generation.
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <Button
                  variant="secondary"
                  onClick={() => setShowGenerateModal(false)}
                >
                  Cancel
                </Button>
                <PermissionGate permission="payroll" action="create">
                  <Button
                    variant="primary"
                    type="button"
                    onClick={handleSubmitGenerate}
                    disabled={isGenerateDisabled || isGeneratingPayslips || isSubmittingRef.current}
                    loading={isGeneratingPayslips}
                    className="bg-agrosaf-green hover:bg-agrosaf-darkGreen disabled:opacity-60"
                  >
                    {isGeneratingPayslips ? 'Generating...' : 'Generate Payslips'}
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Bulk Email Send Confirmation Dialog ─────────────────────────── */}
      {confirmSendBatch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm !mt-0">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-3 px-6 py-5 border-b border-gray-100">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <Mail className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">Send Payslip Emails</h3>
                <p className="text-xs text-gray-500 mt-0.5">This action will send emails to all employees in this batch</p>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              <p className="text-sm text-gray-700">
                You are about to send payslip emails with <strong>PDF attachments</strong> to{' '}
                {confirmSendBatch.totalEmployees > 0 ? (
                  <strong className="text-green-700">{confirmSendBatch.totalEmployees} employee{confirmSendBatch.totalEmployees !== 1 ? 's' : ''}</strong>
                ) : (
                  <strong>all employees</strong>
                )}{' '}
                in <strong>{confirmSendBatch.batchName}</strong>.
              </p>
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  Emails will be sent in the background. You can continue working while they are being sent. This action cannot be undone.
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100">
              <button
                onClick={() => setConfirmSendBatch(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSendBatch}
                className="px-5 py-2 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <Send className="h-4 w-4" />
                Yes, Send Emails
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Bulk Email Job Progress Modal ────────────────────────────────── */}
      {emailJobState && (emailJobState.status === 'processing' || emailJobState.status === 'pending' || emailJobState.status === 'completed' || emailJobState.status === 'failed') && (
        <div className="fixed bottom-6 right-6 z-50 w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-green-600 to-emerald-600">
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-white" />
              <span className="text-sm font-semibold text-white">Sending Payslip Emails</span>
            </div>
            {(emailJobState.status === 'completed' || emailJobState.status === 'failed') && (
              <button
                onClick={() => setEmailJobState(null)}
                className="text-white/70 hover:text-white transition-colors"
                title="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Progress Bar */}
          <div className="px-5 pt-4 pb-2">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>
                {emailJobState.status === 'processing' || emailJobState.status === 'pending'
                  ? 'Processing...'
                  : emailJobState.status === 'completed' ? 'Completed' : 'Failed'}
              </span>
              <span>{emailJobState.sent + emailJobState.failed} / {emailJobState.total}</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
              <div
                className={`h-2.5 rounded-full transition-all duration-500 ${emailJobState.status === 'failed' ? 'bg-red-500' : 'bg-green-500'}`}
                style={{
                  width: emailJobState.total > 0
                    ? `${Math.round(((emailJobState.sent + emailJobState.failed) / emailJobState.total) * 100)}%`
                    : '0%'
                }}
              />
            </div>
          </div>

          {/* Counts */}
          <div className="flex items-center gap-4 px-5 py-3 text-sm">
            <div className="flex items-center gap-1.5 text-green-600">
              <CheckCircle className="h-4 w-4" />
              <span className="font-medium">{emailJobState.sent} sent</span>
            </div>
            {emailJobState.failed > 0 && (
              <div className="flex items-center gap-1.5 text-red-500">
                <AlertCircle className="h-4 w-4" />
                <span className="font-medium">{emailJobState.failed} failed</span>
              </div>
            )}
            {(emailJobState.status === 'processing' || emailJobState.status === 'pending') && (
              <div className="ml-auto flex items-center gap-1.5 text-gray-400">
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                <span className="text-xs">Updating...</span>
              </div>
            )}
          </div>

          {/* Error list */}
          {emailJobState.errors && emailJobState.errors.length > 0 && (
            <div className="border-t border-gray-100 px-5 py-3 max-h-32 overflow-y-auto">
              <p className="text-xs font-medium text-gray-500 mb-1.5">Failed employees:</p>
              {emailJobState.errors.slice(0, 5).map((err, idx) => (
                <div key={idx} className="text-xs text-red-600 mb-0.5">
                  • {err.employeeName || err.employeeId}: {err.error}
                </div>
              ))}
              {emailJobState.errors.length > 5 && (
                <div className="text-xs text-gray-400">+{emailJobState.errors.length - 5} more</div>
              )}
            </div>
          )}

          {/* PDF note */}
          <div className="px-5 pb-4 pt-1">
            <p className="text-xs text-gray-400">📎 Each email includes the payslip as a PDF attachment.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayslipManagement;
